// D02.9 — audio manifest types and validation.
//
// The shipped runtime only reads the committed manifest and its local bytes.
// It never calls a generative service; synthesis happens exclusively through
// the recorded recipe in recipes/audio/slice/.

export interface AudioRef {
  readonly path: string;
  readonly sha256: string;
  readonly bytes: number;
  readonly durationS: number;
  readonly sampleRate: number;
  readonly channels: number;
  readonly bitsPerSample: number;
  readonly format: 'wav' | 'mp3';
}

export type VoiceCategory =
  | 'arrival'
  | 'first-contact'
  | 'contract'
  | 'refusal'
  | 'departure'
  | 'death-absence'
  | 'aftermath'
  | 'story';

export type VoicePresentation = 'living-speaker' | 'narration';

export interface PronunciationNote {
  readonly token: string;
  readonly note: string;
}

export type VoiceProfile =
  | { readonly engine: 'maya1'; readonly model: string; readonly voiceDescription: string }
  | { readonly engine: 'kokoro'; readonly model: string; readonly voice: string; readonly speed: number };

export interface VoiceClip {
  readonly id: string;
  readonly kind: 'voice';
  readonly category: VoiceCategory;
  readonly sourceTextId: string;
  readonly source: { readonly path: string; readonly pointer: string };
  readonly text: string;
  readonly subtitle: string;
  readonly speaker: { readonly id: string; readonly name: string; readonly alive: true };
  readonly presentation: VoicePresentation;
  readonly voice: VoiceProfile;
  readonly pronunciation: readonly PronunciationNote[];
  readonly specHash: string;
  readonly audio: AudioRef;
}

export type SfxKind = 'ambience' | 'foley' | 'habit' | 'ui' | 'combat' | 'dialogue';
export type SfxPresentation = 'environment' | 'material' | 'mechanical-repetition' | 'interface' | 'action';

export interface SfxClip {
  readonly id: string;
  readonly kind: SfxKind;
  readonly prompt: string;
  readonly seed: number;
  readonly durationRequestedS: number;
  readonly steps: number;
  readonly presentation: SfxPresentation;
  readonly spoken: false;
  readonly zone?: string;
  readonly beat?: string;
  readonly material?: string;
  readonly linkedTextId?: string;
  readonly notes?: string;
  readonly trigger?: string;
  readonly layer?: string;
  readonly loop?: boolean;
  readonly defaultGain?: number;
  readonly specHash: string;
  readonly audio: AudioRef;
}

export type MusicRole = 'menu' | 'exploration' | 'combat' | 'resolution';
/** A recipe's own name for when a cue plays (`silt-stair-day`, `quiet-kilns-tension`…): lower-case words joined by hyphens. */
export type MusicState = string;

export interface MusicClip {
  readonly id: string;
  readonly title: string;
  readonly role: MusicRole;
  readonly state: MusicState;
  readonly trigger: string;
  readonly loop: boolean;
  readonly defaultGain: number;
  readonly provider: string;
  readonly recipe: string;
  readonly zone?: string;
  readonly audio: AudioRef;
}

export type FootstepSurface = 'stone' | 'timber' | 'gravel' | 'water-edge';

export interface AudioManifest {
  readonly version: 1;
  readonly milestone: string;
  readonly generator: string;
  readonly recipes: readonly string[];
  readonly recipeHashes: { readonly voice: string; readonly sfx: string; readonly playable?: string; readonly acted?: string };
  readonly providers: Readonly<Record<string, { readonly model: string; readonly endpoint: string; readonly license: string }>>;
  readonly caps: { readonly voiceClips: number; readonly voiceClipsUsed: number; readonly sfxEntries: number };
  readonly clips: readonly VoiceClip[];
  readonly sfx: readonly SfxClip[];
  readonly music: Readonly<Record<string, MusicClip>>;
  readonly surfaces: Readonly<Record<string, FootstepSurface>>;
}

const VOICE_CATEGORIES: readonly VoiceCategory[] = ['arrival', 'first-contact', 'contract', 'refusal', 'departure', 'death-absence', 'aftermath', 'story'];
const VOICE_PRESENTATIONS: readonly VoicePresentation[] = ['living-speaker', 'narration'];
const SFX_KINDS: readonly SfxKind[] = ['ambience', 'foley', 'habit', 'ui', 'combat', 'dialogue'];
const SFX_PRESENTATIONS: readonly SfxPresentation[] = ['environment', 'material', 'mechanical-repetition', 'interface', 'action'];
const MUSIC_ROLES: readonly MusicRole[] = ['menu', 'exploration', 'combat', 'resolution'];
const FOOTSTEP_SURFACES: readonly FootstepSurface[] = ['stone', 'timber', 'gravel', 'water-edge'];

type Json = Record<string, unknown>;

function fail(path: string, message: string): never {
  throw new Error(`audio manifest: ${path}: ${message}`);
}

function obj(value: unknown, path: string): Json {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) fail(path, 'expected an object');
  return value as Json;
}

function str(value: unknown, path: string, allowEmpty = false): string {
  if (typeof value !== 'string' || (!allowEmpty && !value.trim())) fail(path, 'expected a non-empty string');
  return value as string;
}

function num(value: unknown, path: string, min = 0): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min) fail(path, `expected a number >= ${min}`);
  return value as number;
}

function oneOf<T extends string>(value: unknown, path: string, allowed: readonly T[]): T {
  const text = str(value, path);
  if (!allowed.includes(text as T)) fail(path, `expected one of ${allowed.join(', ')}`);
  return text as T;
}

function musicState(value: unknown, path: string): MusicState {
  const text = str(value, path);
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(text)) fail(path, 'expected a lower-case hyphenated id');
  return text;
}

function parseAudioRef(value: unknown, path: string): AudioRef {
  const v = obj(value, path);
  const format = str(v['format'], `${path}.format`);
  if (format !== 'wav' && format !== 'mp3') fail(`${path}.format`, 'expected wav or mp3');
  return {
    path: str(v['path'], `${path}.path`),
    sha256: str(v['sha256'], `${path}.sha256`),
    bytes: num(v['bytes'], `${path}.bytes`, 1),
    durationS: num(v['durationS'], `${path}.durationS`),
    sampleRate: num(v['sampleRate'], `${path}.sampleRate`, 1),
    channels: num(v['channels'], `${path}.channels`, 1),
    bitsPerSample: num(v['bitsPerSample'], `${path}.bitsPerSample`, 1),
    format,
  };
}

function parseVoiceClip(value: unknown, path: string): VoiceClip {
  const v = obj(value, path);
  if (v['kind'] !== 'voice') fail(`${path}.kind`, 'expected voice');
  const speaker = obj(v['speaker'], `${path}.speaker`);
  if (speaker['alive'] !== true) fail(`${path}.speaker.alive`, 'only living speakers may be voiced; the dead are never given new speech');
  const presentation = oneOf(v['presentation'], `${path}.presentation`, VOICE_PRESENTATIONS);
  const text = str(v['text'], `${path}.text`);
  const subtitle = str(v['subtitle'], `${path}.subtitle`);
  if (subtitle !== text) fail(`${path}.subtitle`, 'subtitles must be exact: subtitle and text differ');
  const source = obj(v['source'], `${path}.source`);
  const voice = obj(v['voice'], `${path}.voice`);
  const engine = oneOf(voice['engine'], `${path}.voice.engine`, ['kokoro', 'maya1'] as const);
  const pronunciation = v['pronunciation'];
  if (!Array.isArray(pronunciation)) fail(`${path}.pronunciation`, 'pronunciation metadata must be an array');
  return {
    id: str(v['id'], `${path}.id`),
    kind: 'voice',
    category: oneOf(v['category'], `${path}.category`, VOICE_CATEGORIES),
    sourceTextId: str(v['sourceTextId'], `${path}.sourceTextId`),
    source: { path: str(source['path'], `${path}.source.path`), pointer: str(source['pointer'], `${path}.source.pointer`) },
    text,
    subtitle,
    speaker: { id: str(speaker['id'], `${path}.speaker.id`), name: str(speaker['name'], `${path}.speaker.name`), alive: true },
    presentation,
    voice: engine === 'maya1'
      ? { engine: 'maya1', model: str(voice['model'], `${path}.voice.model`), voiceDescription: str(voice['voiceDescription'], `${path}.voice.voiceDescription`) }
      : { engine: 'kokoro', model: str(voice['model'], `${path}.voice.model`), voice: str(voice['voice'], `${path}.voice.voice`), speed: num(voice['speed'], `${path}.voice.speed`) },
    pronunciation: pronunciation.map((note, i) => {
      const n = obj(note, `${path}.pronunciation[${i}]`);
      return { token: str(n['token'], `${path}.pronunciation[${i}].token`), note: str(n['note'], `${path}.pronunciation[${i}].note`) };
    }),
    specHash: str(v['specHash'], `${path}.specHash`),
    audio: parseAudioRef(v['audio'], `${path}.audio`),
  };
}

function parseSfxClip(value: unknown, path: string): SfxClip {
  const v = obj(value, path);
  const kind = oneOf(v['kind'], `${path}.kind`, SFX_KINDS);
  if (v['spoken'] !== false) fail(`${path}.spoken`, 'sfx entries are never speech');
  const presentation = oneOf(v['presentation'], `${path}.presentation`, SFX_PRESENTATIONS);
  if (kind === 'habit' && presentation !== 'mechanical-repetition') {
    fail(`${path}.presentation`, "a corpse's repeated habit is mechanical repetition, never a new conversation or a restored mind");
  }
  const clip: SfxClip = {
    id: str(v['id'], `${path}.id`),
    kind,
    prompt: str(v['prompt'], `${path}.prompt`),
    seed: num(v['seed'], `${path}.seed`),
    durationRequestedS: num(v['durationRequestedS'], `${path}.durationRequestedS`),
    steps: num(v['steps'], `${path}.steps`, 1),
    presentation,
    spoken: false,
    specHash: str(v['specHash'], `${path}.specHash`),
    audio: parseAudioRef(v['audio'], `${path}.audio`),
  };
  const optional = { zone: v['zone'], beat: v['beat'], material: v['material'], linkedTextId: v['linkedTextId'], notes: v['notes'] } as const;
  const extras: Partial<Pick<SfxClip, keyof typeof optional>> = {};
  for (const [key, raw] of Object.entries(optional) as [keyof typeof optional, unknown][]) {
    if (raw !== undefined) (extras as Record<string, string>)[key] = str(raw, `${path}.${key}`);
  }
  return {
    ...clip,
    ...extras,
    ...(v['trigger'] === undefined ? {} : { trigger: str(v['trigger'], `${path}.trigger`) }),
    ...(v['layer'] === undefined ? {} : { layer: str(v['layer'], `${path}.layer`) }),
    ...(v['loop'] === undefined ? {} : { loop: v['loop'] === true }),
    ...(v['defaultGain'] === undefined ? {} : { defaultGain: num(v['defaultGain'], `${path}.defaultGain`) }),
  };
}

function parseMusicClip(value: unknown, path: string): MusicClip {
  const v = obj(value, path);
  return {
    id: str(v['id'], `${path}.id`),
    title: str(v['title'], `${path}.title`),
    role: oneOf(v['role'], `${path}.role`, MUSIC_ROLES),
    state: musicState(v['state'], `${path}.state`),
    trigger: str(v['trigger'], `${path}.trigger`),
    loop: v['loop'] === true,
    defaultGain: num(v['defaultGain'], `${path}.defaultGain`),
    provider: str(v['provider'], `${path}.provider`),
    recipe: str(v['recipe'], `${path}.recipe`),
    ...(v['zone'] === undefined ? {} : { zone: str(v['zone'], `${path}.zone`) }),
    audio: parseAudioRef(v['audio'], `${path}.audio`),
  };
}

export function parseAudioManifest(value: unknown): AudioManifest {
  const v = obj(value, 'manifest');
  if (v['version'] !== 1) fail('manifest.version', 'expected 1');
  const caps = obj(v['caps'], 'manifest.caps');
  const voiceClips = num(caps['voiceClips'], 'manifest.caps.voiceClips', 1);
  const clipsValue = v['clips'];
  const sfxValue = v['sfx'];
  if (!Array.isArray(clipsValue)) fail('manifest.clips', 'expected an array');
  if (!Array.isArray(sfxValue)) fail('manifest.sfx', 'expected an array');
  if (clipsValue.length > voiceClips) fail('manifest.clips', `voice clip cap exceeded: ${clipsValue.length} > ${voiceClips}`);
  const clips = clipsValue.map((clip, i) => parseVoiceClip(clip, `manifest.clips[${i}]`));
  const sfx = sfxValue.map((clip, i) => parseSfxClip(clip, `manifest.sfx[${i}]`));
  const ids = new Set<string>();
  for (const clip of [...clips, ...sfx]) {
    if (ids.has(clip.id)) fail('manifest', `duplicate clip id ${clip.id}`);
    ids.add(clip.id);
  }
  const recipes = v['recipes'];
  if (!Array.isArray(recipes)) fail('manifest.recipes', 'expected an array');
  const recipeHashes = obj(v['recipeHashes'], 'manifest.recipeHashes');
  const providersValue = obj(v['providers'], 'manifest.providers');
  const providers: Record<string, { model: string; endpoint: string; license: string }> = {};
  for (const [name, raw] of Object.entries(providersValue)) {
    const p = obj(raw, `manifest.providers.${name}`);
    providers[name] = {
      model: str(p['model'], `manifest.providers.${name}.model`),
      endpoint: str(p['endpoint'], `manifest.providers.${name}.endpoint`),
      license: str(p['license'], `manifest.providers.${name}.license`),
    };
  }
  const musicValue = obj(v['music'], 'manifest.music');
  const music: Record<string, MusicClip> = {};
  for (const [name, raw] of Object.entries(musicValue)) music[name] = parseMusicClip(raw, `manifest.music.${name}`);
  const surfacesValue = obj(v['surfaces'], 'manifest.surfaces');
  const surfaces: Record<string, FootstepSurface> = {};
  for (const [area, surface] of Object.entries(surfacesValue)) surfaces[area] = oneOf(surface, `manifest.surfaces.${area}`, FOOTSTEP_SURFACES);
  return {
    version: 1,
    milestone: str(v['milestone'], 'manifest.milestone'),
    generator: str(v['generator'], 'manifest.generator'),
    recipes: recipes.map((r, i) => str(r, `manifest.recipes[${i}]`)),
    recipeHashes: {
      voice: str(recipeHashes['voice'], 'manifest.recipeHashes.voice'),
      sfx: str(recipeHashes['sfx'], 'manifest.recipeHashes.sfx'),
      ...(recipeHashes['playable'] === undefined ? {} : { playable: str(recipeHashes['playable'], 'manifest.recipeHashes.playable') }),
      ...(recipeHashes['acted'] === undefined ? {} : { acted: str(recipeHashes['acted'], 'manifest.recipeHashes.acted') }),
    },
    providers,
    caps: { voiceClips, voiceClipsUsed: num(caps['voiceClipsUsed'], 'manifest.caps.voiceClipsUsed'), sfxEntries: num(caps['sfxEntries'], 'manifest.caps.sfxEntries') },
    clips,
    sfx,
    music,
    surfaces,
  };
}
